/* eslint-disable no-restricted-syntax */
import { Injectable } from '@jack-henry/frontend-utils/di';
import {
    CompanyAccountModelDto,
    FilterAccountModelDto,
    InternalTransferAddTransactionModelDto,
    InternalTransfersClient,
    InternalTransferTransactionFilterModelDto,
    InternalTransferTransactionModelDto,
    ProductPermissionModelDto,
} from '@treasury/api/channel';
import { TmHttpClient } from '@treasury/core/http';
import { CompanyAccount } from '../company/company-account.entity';
import { getLoanPaymentDetails } from './loan-requests';
import { AuditModel, LoanAccount, LoanDto } from './loan.dto';
import { Loan } from './loan.entity';

@Injectable()
export class LoanService {
    constructor(private loanClient: InternalTransfersClient) {}

    public async getLoanPaymentDetails(id: string) {
        const details = (await getLoanPaymentDetails(id)) as InternalTransferTransactionModelDto;
        // map the InternalTransferTransactionModelDto to LoanDto
        const loanDto: LoanDto = {
            ...details,
            fromAccount: details.fromAccount as unknown as LoanAccount,
            toAccount: details.toAccount as unknown as LoanAccount,
            amount: details.amount || 0,
            transferDate: details.transferDate || '',
            memo: details.memo || '',
            status: details.status || '',
            confirmationNumber: details.confirmationNumber || '',
            numberOfApprovalsNeeded: details.numberOfApprovalsNeeded || 0,
            createdDate: details.createdDate || '',
            transactionId: details.transactionId || '',
            lastEditedDate: details.lastEditedDate || '',
            errorSummary: details.errorSummary || null,
            isEdited: false,
            paymentOptionType: details.paymentOptionType || '',
            id: details.id || 0,
            updatedBy: details.updatedBy || '',
            updatedDate: details.updatedDate || '',
            loanPaymentDueDate: null,
            batchId: details.batchId || '',
            message: details.message || null,
            messageType: details.messageType || null,
            comments: details.comments || null,
            frequency: null,
            frequencyDescription: details.frequencyDescription || null,
            nextTransferDate: details.nextTransferDate || null,
            endDate: details.endDate || null,
            startDate: details.startDate || null,
            audit: (details.audit as unknown as AuditModel[]) || ([] as unknown as AuditModel[]),
            permissions:
                (details.permissions as unknown as ProductPermissionModelDto[]) ||
                ([] as unknown as ProductPermissionModelDto[]),
            frequencyModel: (details.frequencyModel as unknown as string) || null,
            parentFrequencyModel: (details.parentFrequencyModel as unknown as string) || null,
            completedApprovals: details.completedApprovals || [],
            availableApprovers: details.availableApprovers || [],
            completedApprovalCount: details.completedApprovalCount || 0,
            securityMessage: (details.securityMessage as unknown as string) || null,
        };
        const loan = new Loan(loanDto);
        loan.fromAccount = new CompanyAccount(details.fromAccount as CompanyAccountModelDto);
        loan.toAccount = new CompanyAccount(details.toAccount as CompanyAccountModelDto);
        return loan;
    }

    public async getLoanPayments(filters: InternalTransferTransactionFilterModelDto) {
        const response = this.loanClient.internalTransfersGetLoanPayments(filters);
        return (await response).data.map(item => new Loan(item as unknown as LoanDto));
    }

    public async getLoanPaymentFromAccounts() {
        const request = (await TmHttpClient.getInstance()).request<FilterAccountModelDto[]>(
            `companyaccounts/GetForProductFeature?productFeatureType=InternalTransfer&reportType=create&isForLoanPayment=true&type=From`,
            {
                method: 'GET',
            }
        );
        const response = await request;
        return response.map(
            account => new CompanyAccount(account as unknown as CompanyAccountModelDto)
        );
    }

    public async getLoanPaymentToAccounts(accountId: string | undefined) {
        const oppositeAccountQuery = accountId ? `&oppositeAccountId=${accountId}` : '';
        const request = (await TmHttpClient.getInstance()).request<FilterAccountModelDto[]>(
            `companyaccounts/GetForProductFeature?productFeatureType=InternalTransfer&reportType=create&isForLoanPayment=true&type=To${oppositeAccountQuery}`,
            {
                method: 'GET',
            }
        );
        const response = await request;
        return response.map(
            account => new CompanyAccount(account as unknown as CompanyAccountModelDto)
        );
    }

    public async createLoanPayment(transaction: Loan) {
        const { data } = (await this.loanClient.internalTransfersPost(
            { type: 'add' },
            {
                ...(transaction.toCreateDto() as unknown as InternalTransferAddTransactionModelDto),
            }
        )) as unknown as { data: InternalTransferAddTransactionModelDto };
        return {
            ...data,
            transactions: data.transactions
                ? data.transactions.map(t => new Loan(t as unknown as LoanDto))
                : [new Loan()],
        };
    }

    public async updateLoanPayment(transaction: Loan) {
        const { data } = (await this.loanClient.internalTransfersPut(transaction.id || 0, {
            ...transaction.toUpdateDto(),
            securityMessage: {
                actionType: 'Saving One time Transfer',
                errorCode: undefined,
                hasAlternate: false,
                message: undefined,
                methodUsed: undefined,
                oneTimePassword: undefined,
                status: undefined,
                challengeMethodTypeId: 0,
            },
        })) as unknown as { data: InternalTransferTransactionModelDto };
        return {
            ...data,
            transactions: new Loan(data as unknown as LoanDto),
        };
    }

    public async cancelLoanPayment(id: number, comments: string) {
        return this.loanClient.internalTransfersCancel({
            internalTransferId: id,
            comments,
            securityMessage: {
                actionType: 'DeleteTransfer_ByName',
                status: undefined,
                methodUsed: undefined,
                hasAlternate: false,
                errorCode: undefined,
                message: undefined,
                oneTimePassword: undefined,
                challengeMethodTypeId: 0,
            },
            updatedDate: '',
            id: 0,
        });
    }

    public async batchApproveLoanPayments(loans: Loan[], comments: string) {
        return this.loanClient.internalTransfersBatchApprove({
            comments,
            internalTransfers: loans.map(loan =>
                loan.toDto()
            ) as unknown as InternalTransferTransactionModelDto[],
            securityMessage: {
                actionType: 'Approve Transfer',
                status: undefined,
                methodUsed: undefined,
                hasAlternate: false,
                errorCode: undefined,
                message: undefined,
                oneTimePassword: undefined,
                challengeMethodTypeId: 0,
            },
            id: 0,
            updatedDate: '',
        });
    }

    public async batchRejectLoanPayments(loans: Loan[], comments: string) {
        return this.loanClient.internalTransfersBatchReject({
            comments,
            internalTransfers: loans.map(loan =>
                loan.toDto()
            ) as unknown as InternalTransferTransactionModelDto[],
            securityMessage: {
                actionType: 'Reject Transfer',
                status: undefined,
                methodUsed: undefined,
                hasAlternate: false,
                errorCode: undefined,
                message: undefined,
                oneTimePassword: undefined,
                challengeMethodTypeId: 0,
            },
            id: 0,
            updatedDate: '',
        });
    }
}
